import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { ArrowLeft, Loader2, KeyRound } from 'lucide-react';
import { api, friendlyError, unwrap } from '@/lib/api';
import { ACCOUNT_STATUS_LABELS, PROFILE_TYPES, ROUTES, ROLE_LABELS } from '@/lib/constants';
import { ADMIN } from '@/constants/testIds';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const STATUS_VALUES = [
  'active',
  'pending_email_verification',
  'pending_guardian_consent',
  'suspended',
  'deleted',
];

export default function AdminUserDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentAdmin } = useAuth();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('');
  const [profileType, setProfileType] = useState('');
  const [savingStatus, setSavingStatus] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  // Password change (Fase 07 extra)
  const [pwdForm, setPwdForm] = useState({ new_password: '', confirm_password: '' });
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdConfirmOpen, setPwdConfirmOpen] = useState(false);
  // Identidad (super_admin) — nombre, apellidos, email
  const [idForm, setIdForm] = useState({ first_name: '', last_name: '', email: '' });
  const [idSaving, setIdSaving] = useState(false);
  const [idConfirmOpen, setIdConfirmOpen] = useState(false);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/admin/users/${id}`);
      const u = unwrap(res);
      setUser(u);
      setStatus(u.account_status);
      setProfileType(u.profile_type);
      setIdForm({
        first_name: u.first_name || '',
        last_name: u.last_name || '',
        email: u.email || '',
      });
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  }

  const saveStatus = async () => {
    if (savingStatus) return;
    setSavingStatus(true);
    try {
      const res = await api.patch(`/admin/users/${id}/status`, { status });
      const updated = unwrap(res);
      setUser(updated);
      toast.success(res?.data?.message || 'Estado actualizado.');
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setSavingStatus(false);
    }
  };

  const saveProfile = async () => {
    if (savingProfile) return;
    setSavingProfile(true);
    try {
      const res = await api.patch(`/admin/users/${id}/profile-type`, { profile_type: profileType });
      const updated = unwrap(res);
      setUser(updated);
      toast.success(res?.data?.message || 'Tipo de perfil actualizado.');
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setSavingProfile(false);
    }
  };

  // -------- Cambio de contraseña por admin --------
  const isSelf = currentAdmin?.id === id;
  const isSuperAdmin = !!currentAdmin?.is_super_admin;

  // -------- Editar identidad (nombre, apellidos, email) por super_admin --------
  const idFormDirty =
    !!user &&
    (
      (idForm.first_name || '').trim() !== (user.first_name || '') ||
      (idForm.last_name || '').trim() !== (user.last_name || '') ||
      (idForm.email || '').trim().toLowerCase() !== (user.email || '').toLowerCase()
    );

  const validateIdentity = () => {
    const fn = (idForm.first_name || '').trim();
    const ln = (idForm.last_name || '').trim();
    const em = (idForm.email || '').trim();
    if (!fn) return 'El nombre no puede estar vacío.';
    if (!ln) return 'Los apellidos no pueden estar vacíos.';
    if (fn.length > 80 || ln.length > 80) return 'Nombre/apellidos demasiado largos.';
    // regex mínimo (validación real la hace el backend con EmailStr)
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) return 'Email no válido.';
    return null;
  };

  const requestIdentityChange = (e) => {
    e.preventDefault();
    if (idSaving || !idFormDirty) return;
    const err = validateIdentity();
    if (err) { toast.error(err); return; }
    setIdConfirmOpen(true);
  };

  const confirmIdentityChange = async () => {
    setIdConfirmOpen(false);
    setIdSaving(true);
    try {
      const patch = {};
      const fn = (idForm.first_name || '').trim();
      const ln = (idForm.last_name || '').trim();
      const em = (idForm.email || '').trim().toLowerCase();
      if (fn !== (user.first_name || '')) patch.first_name = fn;
      if (ln !== (user.last_name || '')) patch.last_name = ln;
      if (em !== (user.email || '').toLowerCase()) patch.email = em;
      const res = await api.patch(`/admin/users/${id}/identity`, patch);
      const updated = unwrap(res);
      setUser(updated);
      setIdForm({
        first_name: updated.first_name || '',
        last_name: updated.last_name || '',
        email: updated.email || '',
      });
      toast.success(res?.data?.message || 'Datos actualizados.');
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setIdSaving(false);
    }
  };

  const validatePwd = () => {
    const { new_password, confirm_password } = pwdForm;
    if (new_password.length < 8) return 'La contraseña debe tener al menos 8 caracteres.';
    if (new_password !== confirm_password) return 'Las contraseñas no coinciden.';
    if (!/[A-Z]/.test(new_password)) return 'La contraseña debe incluir una mayúscula.';
    if (!/[a-z]/.test(new_password)) return 'La contraseña debe incluir una minúscula.';
    if (!/\d/.test(new_password)) return 'La contraseña debe incluir un número.';
    return null;
  };

  const requestPwdChange = (e) => {
    e.preventDefault();
    if (pwdSaving) return;
    if (isSelf) {
      toast.error('No puedes cambiar tu propia contraseña desde aquí. Usa "Mi perfil".');
      return;
    }
    const err = validatePwd();
    if (err) { toast.error(err); return; }
    setPwdConfirmOpen(true);
  };

  const confirmPwdChange = async () => {
    setPwdConfirmOpen(false);
    setPwdSaving(true);
    try {
      const res = await api.patch(`/admin/users/${id}/password`, {
        new_password: pwdForm.new_password,
        confirm_password: pwdForm.confirm_password,
      });
      toast.success(res?.data?.message || 'Contraseña actualizada correctamente.');
      setPwdForm({ new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setPwdSaving(false);
    }
  };

  return (
    <AdminLayout>
      <section>
        <Button
          asChild
          variant="outline"
          data-testid={ADMIN.detailBack}
          className="mb-6 rounded-lg bg-white border-[var(--ns-border-strong)] text-[var(--ns-text)] hover:bg-[var(--ns-amber-50)]"
        >
          <Link to={ROUTES.adminUsers} className="inline-flex items-center gap-2">
            <ArrowLeft className="h-4 w-4" />
            Volver al listado
          </Link>
        </Button>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-7 w-7 animate-spin text-[var(--ns-gold-600)]" />
          </div>
        ) : error ? (
          <p className="text-sm text-[var(--ns-red)]">{error}</p>
        ) : user ? (
          <>
            <span className="text-[11px] uppercase tracking-[0.25em] text-[var(--ns-gold-600)] font-semibold">
              Usuario
            </span>
            <h1 className="font-display uppercase tracking-tight text-[var(--ns-text)] text-3xl sm:text-4xl mt-1 leading-[1.05]">
              {user.first_name} {user.last_name}
            </h1>
            <p data-testid={ADMIN.detailEmail} className="text-sm text-[var(--ns-text-muted)] mt-1">
              {user.email}
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <StatusBadge status={user.account_status} />
              <span className="text-[11px] uppercase tracking-wider text-[var(--ns-text-muted)]">
                Rol: {ROLE_LABELS[user.role] || user.role}
              </span>
            </div>

            <Card className="mt-6 bg-white border border-[var(--ns-border)] rounded-2xl shadow-[var(--ns-shadow-soft)]">
              <CardContent className="p-6 sm:p-8 grid gap-3 grid-cols-1 sm:grid-cols-2">
                <Info label="Fecha de nacimiento" value={user.date_of_birth} />
                <Info label="Creado" value={user.created_at ? new Date(user.created_at).toLocaleDateString('es-ES') : '—'} />
                <Info label="Último login" value={user.last_login_at ? new Date(user.last_login_at).toLocaleString('es-ES') : '—'} />
                <Info label="Tipo de perfil" value={(PROFILE_TYPES.find(p => p.value === user.profile_type)?.label) || user.profile_type} />
                <Info label="Tutor legal" value={user.parent_guardian_name || '—'} />
                <Info label="Email tutor" value={user.parent_guardian_email || '—'} />
                <Info label="Consent. tutor" value={user.guardian_consent_status || '—'} />
                <Info label="Marketing" value={user.marketing_consent ? 'Sí' : 'No'} />
              </CardContent>
            </Card>

            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {/* Cambiar estado */}
              <Card className="bg-white border border-[var(--ns-border)] rounded-2xl shadow-[var(--ns-shadow-soft)]">
                <CardContent className="p-6">
                  <p className="text-[11px] uppercase tracking-widest text-[var(--ns-text-muted)] font-semibold mb-3">
                    Estado de cuenta
                  </p>
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger data-testid={ADMIN.detailStatusSelect} className="h-11 bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_VALUES.map((v) => (
                        <SelectItem key={v} value={v}>{ACCOUNT_STATUS_LABELS[v] || v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={saveStatus}
                    disabled={savingStatus || status === user.account_status}
                    data-testid={ADMIN.detailStatusSave}
                    className="w-full mt-3 h-10 rounded-xl bg-gradient-to-r from-[var(--ns-gold)] to-[var(--ns-gold-600)] text-[#1c1612] font-semibold hover:from-[var(--ns-gold-600)] hover:to-[var(--ns-gold-700)] hover:text-white border-0"
                  >
                    {savingStatus ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Guardar estado'}
                  </Button>
                </CardContent>
              </Card>

              {/* Cambiar profile type */}
              <Card className="bg-white border border-[var(--ns-border)] rounded-2xl shadow-[var(--ns-shadow-soft)]">
                <CardContent className="p-6">
                  <p className="text-[11px] uppercase tracking-widest text-[var(--ns-text-muted)] font-semibold mb-3">
                    Tipo de perfil
                  </p>
                  <Select value={profileType} onValueChange={setProfileType}>
                    <SelectTrigger data-testid={ADMIN.detailProfileSelect} className="h-11 bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PROFILE_TYPES.map((p) => (
                        <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={saveProfile}
                    disabled={savingProfile || profileType === user.profile_type}
                    data-testid={ADMIN.detailProfileSave}
                    className="w-full mt-3 h-10 rounded-xl bg-gradient-to-r from-[var(--ns-gold)] to-[var(--ns-gold-600)] text-[#1c1612] font-semibold hover:from-[var(--ns-gold-600)] hover:to-[var(--ns-gold-700)] hover:text-white border-0"
                  >
                    {savingProfile ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Guardar perfil'}
                  </Button>
                </CardContent>
              </Card>
              {/* Cambio de contraseña por admin (Fase 07 extra) */}
              {isSuperAdmin ? (
              <Card className="bg-white border border-[var(--ns-border)] rounded-2xl shadow-[var(--ns-shadow-soft)]">
                <CardContent className="p-6">
                  <p className="text-[11px] uppercase tracking-widest text-[var(--ns-text-muted)] font-semibold mb-3">
                    Identidad del usuario
                  </p>
                  <form onSubmit={requestIdentityChange} className="flex flex-col gap-3" noValidate>
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="admin-id-first" className="text-xs font-medium text-[var(--ns-text)]">Nombre</Label>
                      <Input
                        id="admin-id-first"
                        value={idForm.first_name}
                        onChange={(e) => setIdForm((f) => ({ ...f, first_name: e.target.value }))}
                        data-testid="admin-user-identity-first-name"
                        maxLength={80}
                        className="h-10 bg-white"
                        required
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="admin-id-last" className="text-xs font-medium text-[var(--ns-text)]">Apellidos</Label>
                      <Input
                        id="admin-id-last"
                        value={idForm.last_name}
                        onChange={(e) => setIdForm((f) => ({ ...f, last_name: e.target.value }))}
                        data-testid="admin-user-identity-last-name"
                        maxLength={80}
                        className="h-10 bg-white"
                        required
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="admin-id-email" className="text-xs font-medium text-[var(--ns-text)]">Email (login)</Label>
                      <Input
                        id="admin-id-email"
                        type="email"
                        value={idForm.email}
                        onChange={(e) => setIdForm((f) => ({ ...f, email: e.target.value }))}
                        data-testid="admin-user-identity-email"
                        autoComplete="off"
                        className="h-10 bg-white"
                        required
                      />
                    </div>
                    <p className="text-[11px] text-[var(--ns-text-subtle)]">
                      El nuevo email pasa a ser el usado para iniciar sesión inmediatamente. Se
                      mantienen el mismo <strong>ID de usuario</strong> y el mismo <strong>rol</strong>.
                      No se modifica la contraseña.
                    </p>
                    <Button
                      type="submit"
                      disabled={idSaving || !idFormDirty}
                      data-testid="admin-user-identity-save"
                      className="w-full mt-1 h-10 rounded-xl bg-gradient-to-r from-[var(--ns-gold)] to-[var(--ns-gold-600)] text-[#1c1612] font-semibold hover:from-[var(--ns-gold-600)] hover:to-[var(--ns-gold-700)] hover:text-white border-0"
                    >
                      {idSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Guardar identidad'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
              ) : null}
              {/* Cambio de contraseña por admin (Fase 07 extra) */}
              <Card className="bg-white border border-[var(--ns-border)] rounded-2xl shadow-[var(--ns-shadow-soft)]">
                <CardContent className="p-6">
                  <p className="text-[11px] uppercase tracking-widest text-[var(--ns-text-muted)] font-semibold mb-3 flex items-center gap-1.5">
                    <KeyRound className="h-3.5 w-3.5 text-[var(--ns-gold-700)]" />
                    Cambiar contraseña del usuario
                  </p>
                  {isSelf ? (
                    <p className="text-xs text-[var(--ns-text-muted)] p-3 rounded-lg bg-[var(--ns-amber-50)] border border-[var(--ns-amber-200)]">
                      No puedes cambiar tu propia contraseña desde el panel de
                      administración. Usa la sección <strong>Mi perfil</strong> en tu
                      dashboard.
                    </p>
                  ) : (
                    <form onSubmit={requestPwdChange} className="flex flex-col gap-3" noValidate>
                      <div className="flex flex-col gap-1.5">
                        <Label htmlFor="admin-pwd-new" className="text-xs font-medium text-[var(--ns-text)]">Nueva contraseña</Label>
                        <Input
                          id="admin-pwd-new"
                          type="password"
                          value={pwdForm.new_password}
                          onChange={(e) => setPwdForm((f) => ({ ...f, new_password: e.target.value }))}
                          data-testid={ADMIN.detailPwdNew}
                          autoComplete="new-password"
                          className="h-10 bg-white"
                          required
                        />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <Label htmlFor="admin-pwd-confirm" className="text-xs font-medium text-[var(--ns-text)]">Confirmar nueva contraseña</Label>
                        <Input
                          id="admin-pwd-confirm"
                          type="password"
                          value={pwdForm.confirm_password}
                          onChange={(e) => setPwdForm((f) => ({ ...f, confirm_password: e.target.value }))}
                          data-testid={ADMIN.detailPwdConfirm}
                          autoComplete="new-password"
                          className="h-10 bg-white"
                          required
                        />
                      </div>
                      <p className="text-[11px] text-[var(--ns-text-subtle)]">
                        Mínimo 8 caracteres con mayúsculas, minúsculas y un número. El usuario podrá
                        iniciar sesión con la nueva contraseña inmediatamente.
                      </p>
                      <Button
                        type="submit"
                        disabled={pwdSaving || !pwdForm.new_password || !pwdForm.confirm_password}
                        data-testid={ADMIN.detailPwdSave}
                        className="w-full mt-1 h-10 rounded-xl bg-gradient-to-r from-[var(--ns-gold)] to-[var(--ns-gold-600)] text-[#1c1612] font-semibold hover:from-[var(--ns-gold-600)] hover:to-[var(--ns-gold-700)] hover:text-white border-0"
                      >
                        {pwdSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Guardar nueva contraseña'}
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>
          </>
        ) : null}
      </section>

      {/* Diálogo de confirmación de cambio de contraseña */}
      <AlertDialog open={pwdConfirmOpen} onOpenChange={setPwdConfirmOpen}>
        <AlertDialogContent data-testid={ADMIN.detailPwdConfirmDialog}>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar cambio de contraseña</AlertDialogTitle>
            <AlertDialogDescription>
              Vas a establecer una nueva contraseña para
              {user ? <strong> {user.first_name} {user.last_name} ({user.email})</strong> : ' este usuario'}.
              El usuario podrá iniciar sesión con la nueva contraseña inmediatamente.
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmPwdChange}
              data-testid={ADMIN.detailPwdConfirmDialogYes}
              className="bg-gradient-to-r from-[var(--ns-gold)] to-[var(--ns-gold-600)] text-[#1c1612] font-semibold hover:from-[var(--ns-gold-600)] hover:to-[var(--ns-gold-700)] hover:text-white border-0"
            >
              Sí, cambiar contraseña
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* Diálogo de confirmación de cambio de identidad (super_admin) */}
      <AlertDialog open={idConfirmOpen} onOpenChange={setIdConfirmOpen}>
        <AlertDialogContent data-testid="admin-user-identity-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar cambio de identidad</AlertDialogTitle>
            <AlertDialogDescription>
              Vas a actualizar los datos de identidad de
              {user ? <strong> {user.first_name} {user.last_name} ({user.email})</strong> : ' este usuario'}.
              El <strong>ID</strong> y el <strong>rol</strong> se mantienen. Si cambias el email,
              se convertirá inmediatamente en el email de login. No se modifica la contraseña.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmIdentityChange}
              data-testid="admin-user-identity-confirm-yes"
              className="bg-gradient-to-r from-[var(--ns-gold)] to-[var(--ns-gold-600)] text-[#1c1612] font-semibold hover:from-[var(--ns-gold-600)] hover:to-[var(--ns-gold-700)] hover:text-white border-0"
            >
              Sí, guardar cambios
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}

const Info = ({ label, value }) => (
  <div>
    <p className="text-[10px] uppercase tracking-widest text-[var(--ns-text-subtle)] font-semibold">{label}</p>
    <p className="text-sm text-[var(--ns-text)] font-medium">{value}</p>
  </div>
);
